/**
 * Status options and mappings for billing tabs
 */

// Unbilled Sessions Tab statuses (direct encounter statuses from API)
export type UnbilledStatus = 'pending' | 'processing' | 'completed';

// Consolidated status options for display and filtering
export const CONSOLIDATED_STATUS_OPTIONS = [
  { value: 'ready_to_bill', label: 'Billable', color: 'primary' as const },
  { value: 'note_not_signed', label: 'Unsigned Note', color: 'default' as const },
  { value: 'processing', label: 'Processing', color: 'info' as const },
  { value: 'pending', label: 'Pending', color: 'default' as const },
] as const;

// Helper function to derive consolidated status
export function deriveConsolidatedStatus(
  encounterStatus: UnbilledStatus,
  billable: boolean
): string {
  if (encounterStatus !== 'completed') {
    return encounterStatus; // 'pending' or 'processing'
  }
  return billable ? 'ready_to_bill' : 'note_not_signed';
}

// Map consolidated status filter to API parameters
export function mapStatusFilterToAPI(consolidatedStatus: string): {
  encounterStatus?: string;
  billable?: boolean;
} {
  switch (consolidatedStatus) {
    case 'pending':
      return { encounterStatus: 'pending' };
    case 'processing':
      return { encounterStatus: 'processing' };
    case 'note_not_signed':
      return { encounterStatus: 'completed', billable: false };
    case 'ready_to_bill':
      return { encounterStatus: 'completed', billable: true };
    default:
      return {};
  }
}

// Claims Tab statuses (from API)
export type ClaimStatus = 'draft' | 'processing' | 'scrubbing' | 'ready' | 'submitted' | 'acknowledged' | 'accepted' | 'rejected';

export const CLAIM_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'default' as const },
  { value: 'processing', label: 'Processing', color: 'warning' as const },
  { value: 'scrubbing', label: 'Processing', color: 'warning' as const },
  { value: 'ready', label: 'Ready', color: 'success' as const },
  { value: 'submitted', label: 'Submitted', color: 'info' as const },
  { value: 'acknowledged', label: 'Received', color: 'primary' as const },
  { value: 'accepted', label: 'Accepted', color: 'success' as const },
  { value: 'rejected', label: 'Rejected', color: 'error' as const },
] as const;

// Superbills Tab statuses (TBD - placeholder)
export type SuperbillStatus = 'completed' | 'draft';

export const SUPERBILL_STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed', color: 'success' as const },
  { value: 'draft', label: 'Draft', color: 'default' as const },
] as const;

// Status options by tab
export const STATUS_OPTIONS_BY_TAB = {
  unbilled: CONSOLIDATED_STATUS_OPTIONS,
  claims: CLAIM_STATUS_OPTIONS,
  superbills: SUPERBILL_STATUS_OPTIONS,
} as const;

// Get status color for display
export function getStatusColor(
  tab: 'unbilled' | 'claims' | 'superbills',
  status: string,
): 'primary' | 'success' | 'warning' | 'error' | 'info' | 'default' {
  const options = STATUS_OPTIONS_BY_TAB[tab];
  const option = options.find(opt => opt.value === status);
  return option?.color || 'default';
}
